/**
 * A game the player coaches (slice 8): Team A is the player's, Team B the AI's. The game lives in
 * the sim as seed + decisions; every call the session replays that list with `playUntil`, hands the
 * new events to the screen, and asks the player for the pending call. The player's answer and the
 * AI's become one `SnapDecision` through the sim's `callOffense` / `callDefense`, the same order
 * `playGameWith` uses, so `playGame(seed, teams, decisions)` replays this game exactly. Nothing here
 * decides an outcome.
 */
import {
  DEFAULT_PLAYBOOK,
  DEFENSE_SLOTS,
  OFFENSE_SLOTS,
  aiCoach,
  applyWindow,
  balance,
  callDefense,
  callOffense,
  playUntil,
  type CallView,
  type Coach,
  type DefenseDecision,
  type Difficulty,
  type Game,
  type GameEvent,
  type OffenseDecision,
  type SnapDecision,
  type SubWindow,
  type Team,
  type TeamId,
} from '@tdmons/sim';
import { kittedBalancedTeams } from '@tdmons/sim/fixtures/balanced-teams';
import type { AiChange } from './aiChanges';
import { newDraft, toDecision, type Draft } from './draft';

export type Side = 'offense' | 'defense';

/** What the player is asked for next. */
export type Prompt =
  | { kind: 'final' }
  | {
      kind: 'call';
      side: Side;
      view: CallView;
      /** The game as the player sees it: after the AI offense's sub window when A defends. */
      game: Readonly<Game>;
      /** The offense's 4th down: go, punt, or kick (the choice is the offense's). */
      fourthDown: boolean;
      /** The try after a touchdown. */
      conversion: boolean;
      /** A replay: the call is already made; `playScripted` plays it. */
      scripted?: SnapDecision;
      /**
       * When the player defends: the AI offense's subs and moves this call, already in `game`,
       * to be said before the player calls (slice 11c). Empty when it changed nobody.
       */
      aiChanges?: readonly AiChange[];
    };

/** The player's half of a call: a decision for the side the player is on. */
export type PlayerCall = OffenseDecision | DefenseDecision;

/** The player's team. */
export const PLAYER: TeamId = 'A';
/** The AI's level when none is picked. */
export const DEFAULT_LEVEL: Difficulty = 'pro';

/** A coach that answers one call with the player's decision, its window through `subs`. */
function playerCoach(call: PlayerCall): Coach {
  const { subs, rest, bag, ...rest_ } = call;
  const window: SubWindow<string> = {
    ...(subs?.length ? { subs } : {}),
    ...(rest?.length ? { rest } : {}),
    ...(bag?.length ? { bag } : {}),
  };
  return {
    offense: () => rest_ as OffenseDecision,
    defense: () => rest_ as DefenseDecision,
    subs: () => window,
  };
}

/** The game events that start a new possession: Hurry's last play does not carry past them. */
const NEW_POSSESSION: ReadonlySet<GameEvent['type']> = new Set([
  'possession',
  'kickoff',
  'overtime',
]);

/**
 * The AI offense's subs and moves this call, as the engine will log them at the snap: its window
 * through the sim's `applyWindow` on the game before it (slice 11c).
 */
function aiChangesOf(view: CallView, offense: OffenseDecision): AiChange[] {
  if (!offense.subs?.length) return [];
  const team = view.game.teams[view.game.possession];
  return applyWindow(team, 'offense', { subs: offense.subs }).events.filter(
    (e): e is AiChange => e.type === 'sub',
  );
}

/** Spend nothing anywhere: a call with no spends chosen must not fire the play's default script. */
function noSpends(slots: readonly string[]): Record<string, null> {
  return Object.fromEntries(slots.map((s) => [s, null]));
}

export class GameSession {
  readonly teams: Record<TeamId, Team>;
  readonly decisions: SnapDecision[] = [];
  /** Every game event so far, in order. */
  readonly events: GameEvent[] = [];
  private readonly ai: Coach;
  /** The AI offense's half of the pending call, made once (its rng draws once per call). */
  private aiOffense: { call: number; offense: OffenseDecision; game: Game } | undefined;
  private pending: CallView | undefined;
  private readonly lastPlay: Partial<Record<Side, string>> = {};

  /**
   * `script`: decisions from an earlier game on this seed, replayed call by call before the
   * player takes over (the Dev tab's Replay).
   */
  constructor(
    readonly seed: number,
    readonly level: Difficulty = DEFAULT_LEVEL,
    teams: Record<TeamId, Team> = kittedBalancedTeams(),
    private readonly script: readonly SnapDecision[] = [],
  ) {
    this.teams = teams;
    this.ai = aiCoach(4 * seed + 4, level);
  }

  /**
   * The last play the player called on this side, or the playbook's default: for the scripted
   * paths and tests. The player's own calls start with no play (`draftFor`).
   */
  playFor(side: Side): string {
    const plays = side === 'offense' ? DEFAULT_PLAYBOOK.offense : DEFAULT_PLAYBOOK.defense;
    return this.lastPlay[side] ?? (plays[0] as { id: string }).id;
  }

  /**
   * Play to the player's next call: the events since the last time, and what the player decides.
   * When the AI's offense kicks (punt, field goal, extra point) there is nothing for the defense
   * to call, so the session answers for it and plays on.
   */
  next(): { events: GameEvent[]; prompt: Prompt } {
    const fresh: GameEvent[] = [];
    for (;;) {
      const r = playUntil(this.seed, this.teams, this.decisions);
      // Hurry's play is this possession's (slice 11c): a new one starts with none.
      if (r.events.slice(this.events.length).some((e) => NEW_POSSESSION.has(e.type))) {
        delete this.lastPlay.offense;
      }
      fresh.push(...r.events.slice(this.events.length));
      this.events.push(...r.events.slice(this.events.length));
      this.pending = r.pending;
      const view = r.pending;
      if (!view) return { events: fresh, prompt: { kind: 'final' } };
      const fourthDown = view.kind === 'scrimmage' && view.game.down === balance.football.downs;
      const conversion = view.kind === 'conversion';
      const scripted = this.script[this.decisions.length];
      if (scripted) {
        const side: Side = view.game.possession === PLAYER ? 'offense' : 'defense';
        return {
          events: fresh,
          prompt: { kind: 'call', side, view, game: view.game, fourthDown, conversion, scripted },
        };
      }
      if (view.game.possession === PLAYER) {
        return {
          events: fresh,
          prompt: { kind: 'call', side: 'offense', view, game: view.game, fourthDown, conversion },
        };
      }
      const ai = this.aiOffenseFor(view);
      const kicks = conversion
        ? (ai.offense.conversion ?? 'kick') === 'kick'
        : fourthDown && ai.offense.fourthDown !== undefined && ai.offense.fourthDown !== 'go';
      if (!kicks) {
        return {
          events: fresh,
          prompt: {
            kind: 'call',
            side: 'defense',
            view,
            game: ai.game,
            fourthDown,
            conversion,
            aiChanges: aiChangesOf(view, ai.offense),
          },
        };
      }
      this.decisions.push(callDefense(view, ai.game, ai.offense, playerCoach({})));
      this.aiOffense = undefined;
    }
  }

  /** The player's call for the pending snap; returns the whole decision it became. */
  submit(call: PlayerCall): SnapDecision {
    const view = this.pending;
    if (!view) throw new Error('the game is over: there is no call to make');
    const side: Side = view.game.possession === PLAYER ? 'offense' : 'defense';
    const slots = side === 'offense' ? OFFENSE_SLOTS : DEFENSE_SLOTS;
    const mine = { moves: noSpends(slots), ...call } as PlayerCall;
    let d: SnapDecision;
    if (side === 'offense') {
      const { offense, game } = callOffense(view, playerCoach(mine));
      d = callDefense(view, game, offense, this.ai);
    } else {
      const ai = this.aiOffenseFor(view);
      d = callDefense(view, ai.game, ai.offense, playerCoach(mine));
    }
    if (mine.playId) this.lastPlay[side] = mine.playId;
    this.decisions.push(d);
    this.pending = undefined;
    this.aiOffense = undefined;
    return d;
  }

  /** Play the replayed decision for the pending call. */
  playScripted(): SnapDecision {
    const d = this.script[this.decisions.length];
    if (!this.pending || !d) throw new Error('no replayed call is pending');
    this.decisions.push(d);
    this.pending = undefined;
    return d;
  }

  /** Snap the last play called on this side (the default at first): no spends, no window. */
  snapLast(side: Side): SnapDecision {
    return this.submit({ playId: this.playFor(side) });
  }

  /**
   * A fresh draft for this call: nothing set, the play included (slice 11c: the player always
   * picks; the Book opens on Plays).
   */
  draftFor(prompt: Extract<Prompt, { kind: 'call' }>): Draft {
    return newDraft(PLAYER, prompt.side);
  }

  /** Hurry's play: the player's last on offense in this possession, or null (slice 11c). */
  hurryPlay(): string | null {
    return this.lastPlay.offense ?? null;
  }

  /** Snap the drafted call: its play, spends, subs, Rest and bag, as one decision. */
  snap(draft: Draft): SnapDecision {
    return this.submit(toDecision(draft));
  }

  /**
   * Hurry-up, offense only: both sub windows skipped. With a draft, its play (or, with none
   * picked, this possession's last) and spends at hurry-up tempo (its subs, Rest and bag are
   * dropped); without one, this possession's last play, no spends.
   */
  hurry(draft?: Draft): SnapDecision {
    if (this.pending?.game.possession !== PLAYER) throw new Error('hurry-up is the offense’s call');
    const playId = draft?.playId ?? this.hurryPlay();
    if (playId === null) throw new Error('no play to hurry: the Book opens on Plays first');
    const call: OffenseDecision = draft
      ? (toDecision({ ...draft, playId }, 'hurry-up') as OffenseDecision)
      : { playId, tempo: 'hurry-up' };
    return this.submit(call);
  }

  private aiOffenseFor(view: CallView): { offense: OffenseDecision; game: Game } {
    if (this.aiOffense?.call !== view.call) {
      const { offense, game } = callOffense(view, this.ai);
      this.aiOffense = { call: view.call, offense, game };
    }
    return this.aiOffense;
  }
}
